/**
 * policies-api.ts — typed feature wrapper + wire→view-model mapping for the
 * Policen list (Story UI-U3, AC 4).
 *
 * The Policen screen went live in this story. Two shapes must be tolerated at
 * once because a PARALLEL backend wave (feat/backend-gaps-wave) is adding
 * premium / Betreuer / Branche / Risiko fields + pagination meta to the wire:
 *   - the CURRENT shape: a plain `PolicyDto[]` array (no cursor, no premium);
 *   - the FUTURE shape: `{ items: PolicyDto[], page: { next_cursor } }` with the
 *     optional fields on each row.
 * This client layer does DISPLAY-shape tolerance only — no domain rules. The
 * optional fields are probed STRUCTURALLY (unknown + narrow, never `any`), so a
 * row that lacks them simply renders `—` / hides its column.
 *
 * TODO(backend-gaps-wave): once the wave merges, collapse `unwrapPolicyList` +
 * the `optField` probes to typed PolicyDto fields + a typed list envelope.
 *
 * Display maps (POLICY_VIEW_STATUS / SPARTE_LABEL) are duplicated locally on
 * purpose — the story forbids importing another feature's internals; these tiny
 * maps mirror the entity-detail semantics.
 *
 * [Source: apps/web/src/features/customers/api/customers-api.ts] (wrapper pattern)
 * [Source: apps/web/src/features/entity-detail/api/entity-detail-api.ts] (display-map semantics)
 * [Source: docs/stories/ui-u3-core-restyle.md AC4]
 */

import { apiClient } from '../../../lib/api-client';
import type { PolicyListParams } from '../../../lib/api-client';
import { dateShortDe, daysUntil } from '../../../lib/format';
import type { LineOfBusiness, PolicyDto, PolicyStatus as WirePolicyStatus } from '@brokly/shared';
import type { PolicyStatus } from '../../entity-detail/types';

// Re-export for convenience
export type { PolicyListParams };

/**
 * PolicyListRow — the Policen-grid view-model. Distinct from the entity-detail
 * `Policy` because premium is NULLABLE here (absent → render `—`, never a fake
 * 0) and it carries the backend-wave optional fields.
 */
export interface PolicyListRow {
  id: string;
  nr: string;
  kunde: string;
  kundeId: string;
  versicherer: string;
  /** carrier_uid — kept for navigation; the display name is overlaid in the hook. */
  versichererId?: string;
  sparte: string;
  /** null = premium not on the wire yet → the cell renders `—` (never a fake 0). */
  praemie: number | null;
  status: PolicyStatus;
  renewalTage: number;
  beginn: string;
  /** Backend-wave optional fields (structurally probed; undefined ⇒ column hidden). */
  betreuer?: string;
  branche?: string;
  risiko?: boolean;
}

// ── Display maps (local, mirror entity-detail semantics) ───────────────────────

/** Wire policy status → the 4-value view status (display map only). */
const POLICY_VIEW_STATUS: Record<WirePolicyStatus, PolicyStatus> = {
  draft: 'sistiert',
  active: 'aktiv',
  in_mutation: 'aktiv',
  renewal_pending: 'renewal',
  cancelled: 'gekuendigt',
  expired: 'gekuendigt',
};

/** line_of_business → German display label. */
const SPARTE_LABEL: Record<LineOfBusiness, string> = {
  uvg: 'UVG',
  ktg: 'KTG',
  bu: 'BU',
  leben: 'Leben',
  sach: 'Sach',
  haftpflicht: 'Haftpflicht',
  kranken: 'Kranken',
  other: 'Übrige',
};

export function sparteLabel(lob: LineOfBusiness | null): string {
  return lob ? SPARTE_LABEL[lob] : '-';
}

// ── Tolerant unwrap (array shape OR future { items, page } envelope) ────────────

export interface PolicyListPage {
  items: unknown[];
  nextCursor: string | null;
}

/**
 * Accept BOTH the current `PolicyDto[]` array AND a future
 * `{ items, page: { next_cursor } }` envelope. Anything else → empty page.
 */
export function unwrapPolicyList(res: unknown): PolicyListPage {
  if (Array.isArray(res)) return { items: res, nextCursor: null };
  if (res !== null && typeof res === 'object' && 'items' in res) {
    const items = (res as { items: unknown }).items;
    if (Array.isArray(items)) {
      const page = (res as { page?: unknown }).page;
      let nextCursor: string | null = null;
      if (page !== null && typeof page === 'object' && 'next_cursor' in page) {
        const nc = (page as { next_cursor: unknown }).next_cursor;
        nextCursor = typeof nc === 'string' && nc.length > 0 ? nc : null;
      }
      return { items, nextCursor };
    }
  }
  return { items: [], nextCursor: null };
}

// ── Structural field probes (no `any`; undefined when absent/wrong-typed) ───────

/** Candidate keys the backend wave might carry the numeric premium under. */
const PREMIUM_KEYS = ['premium', 'premium_gross', 'premium_sum', 'praemie'] as const;

function optRecord(row: unknown): Record<string, unknown> | undefined {
  return row !== null && typeof row === 'object' ? (row as Record<string, unknown>) : undefined;
}

/** First present numeric-like (number or decimal string) premium field → number, else undefined. */
export function optPremium(row: unknown): number | undefined {
  const rec = optRecord(row);
  if (!rec) return undefined;
  for (const key of PREMIUM_KEYS) {
    const v = rec[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return undefined;
}

/** Non-empty string field → trimmed value, else undefined. */
export function optString(row: unknown, key: string): string | undefined {
  const rec = optRecord(row);
  if (!rec) return undefined;
  const v = rec[key];
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

/** Boolean flag field → value, else undefined (absent ⇒ column hidden). */
export function optBool(row: unknown, key: string): boolean | undefined {
  const rec = optRecord(row);
  if (!rec) return undefined;
  const v = rec[key];
  return typeof v === 'boolean' ? v : undefined;
}

// ── Row mapper ─────────────────────────────────────────────────────────────────

/** One wire row (PolicyDto, possibly + backend-wave fields) → the grid view-model. */
export function mapPolicyRow(row: unknown): PolicyListRow {
  // Known fields: api-client types the wire as PolicyDto, so these are present.
  const dto = row as PolicyDto;
  const premium = optPremium(row);
  return {
    id: dto.id,
    nr: dto.policy_number,
    // Kunde/Versicherer display names are overlaid in the hook; ids kept for nav.
    kunde: dto.subject_id,
    kundeId: dto.subject_id,
    versicherer: dto.carrier_uid ?? '-',
    versichererId: dto.carrier_uid ?? undefined,
    sparte: sparteLabel(dto.line_of_business),
    praemie: premium ?? null,
    status: POLICY_VIEW_STATUS[dto.status],
    renewalTage: daysUntil(dto.expiry_date) ?? -1,
    beginn: dto.inception_date ? dateShortDe(dto.inception_date) : '-',
    betreuer: optString(row, 'betreuer'),
    branche: optString(row, 'branche'),
    risiko: optBool(row, 'risiko'),
  };
}

/** Fetch + tolerantly map the Policen rows (GET /policies). Returns rows + cursor. */
export async function fetchPolicyList(
  params: PolicyListParams = {},
): Promise<{ items: PolicyListRow[]; nextCursor: string | null }> {
  const res: unknown = await apiClient.policies.list(params);
  const { items, nextCursor } = unwrapPolicyList(res);
  return { items: items.map(mapPolicyRow), nextCursor };
}
